import { useEffect, useRef } from 'react';
import L from 'leaflet';
import { animateMarker } from '@/pages/Business/pages/DriverMonitoring/utils/animateMarker';
import { createGpsPulseMarkerIcon } from '../components/GpsPulseMarkerIcon';
import { DriverGpsPosition } from '../models/DriverGpsPosition';

export interface UseLiveDriverMarkerOptions {
  duration?: number;
}

function isMapUsable(map: L.Map): boolean {
  const container = map.getContainer?.();
  return Boolean(container && document.contains(container));
}

export const useLiveDriverMarker = (
  mapInstance: L.Map | null,
  position: DriverGpsPosition | undefined,
  options: UseLiveDriverMarkerOptions = {}
): void => {
  const { duration = 2000 } = options;
  const markerRef = useRef<L.Marker | null>(null);
  const markerMapRef = useRef<L.Map | null>(null);

  useEffect(() => {
    return () => {
      if (markerRef.current) {
        try {
          markerRef.current.remove();
        } catch {
          /* el mapa ya pudo haber sido destruido */
        }
        markerRef.current = null;
        markerMapRef.current = null;
      }
    };
  }, [mapInstance]);

  useEffect(() => {
    if (!mapInstance || !position) return;
    if (!isMapUsable(mapInstance)) return;

    const newLatLng: L.LatLngExpression = [
      position.latitude,
      position.longitude,
    ];

    if (!markerRef.current || markerMapRef.current !== mapInstance) {
      if (markerRef.current) {
        try {
          markerRef.current.remove();
        } catch {
          /* el mapa anterior ya pudo haber sido destruido */
        }
      }

      try {
        markerRef.current = L.marker(newLatLng, {
          icon: createGpsPulseMarkerIcon(),
          zIndexOffset: 1000,
          interactive: false,
        }).addTo(mapInstance);
        markerMapRef.current = mapInstance;

        const driverFocusZoom = Math.max(mapInstance.getZoom(), 15);
        mapInstance.setView(newLatLng, driverFocusZoom, { animate: false });
      } catch {
        markerRef.current = null;
        markerMapRef.current = null;
      }
      return;
    }

    try {
      const currentLatLng = markerRef.current.getLatLng();
      const positionChanged =
        currentLatLng.lat !== position.latitude ||
        currentLatLng.lng !== position.longitude;

      if (positionChanged) {
        animateMarker(markerRef.current, newLatLng, { duration });
      }
    } catch {
      /* el marcador o el mapa ya pudieron haber sido destruidos */
    }
  }, [mapInstance, position?.latitude, position?.longitude, duration]);
};
